import { TbBrandDiscordFilled, TbBrandLinkedin, TbBrandYoutube } from "solid-icons/tb";
import { type Component, For } from "solid-js";
import { SlackIcon } from "~/components/knowledge/pages/ScopePickerEmptyState";
import { signupCredits } from "~/components/marketing/ClosingCTA";
import { SOCIAL_LINKS } from "~/components/marketing/MarketingFooter";
import { SectionTitle } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import "../home.css";

const COMMUNITY: {
  network: string;
  icon: Component<{ class?: string }>;
  color: string;
  description: TranslationKey;
}[] = [
  { network: "Slack", icon: SlackIcon, color: "#4A154B", description: "marketing.home.community.slack" },
  { network: "Discord", icon: TbBrandDiscordFilled, color: "#5865F2", description: "marketing.home.community.discord" },
  { network: "LinkedIn", icon: TbBrandLinkedin, color: "#0A66C2", description: "marketing.home.community.linkedin" },
  { network: "YouTube", icon: TbBrandYoutube, color: "#FF0000", description: "marketing.home.community.youtube" },
];

export const CommunitySection = () => {
  const { t } = useI18n();
  const links = () =>
    COMMUNITY.flatMap((item) => {
      const link = SOCIAL_LINKS.find((social) => social.label === item.network);
      return link ? [{ ...item, href: link.href }] : [];
    });

  return (
    <section aria-labelledby="home-community" class="px-4 py-20 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <SectionTitle id="home-community" text={t("marketing.home.community.title")} linked />
        <ul class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <For each={links()}>
            {(link) => (
              <li>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ "--logo": link.color }}
                  class="group flex h-full flex-col gap-3 rounded-2xl border border-primary-border bg-tile p-6 transition duration-200 hover:-translate-y-1 hover:border-(--logo)/40 hover:shadow-[0_16px_40px_-20px_var(--logo)] focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                >
                  <span
                    aria-hidden="true"
                    class="logo-ink grid size-12 place-items-center rounded-xl bg-(--logo)/10 ring-1 ring-(--logo)/15 dark:bg-(--logo)/20"
                  >
                    <link.icon class="size-6" />
                  </span>
                  <span class="mt-1 flex items-center gap-1.5 font-semibold">
                    {link.network}
                    <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">
                      →
                    </span>
                  </span>
                  <span class="text-sm leading-relaxed text-secondary-text">{t(link.description)}</span>
                </a>
              </li>
            )}
          </For>
        </ul>
      </div>
    </section>
  );
};

const FAQ: { q: TranslationKey; a: TranslationKey }[] = [
  { q: "marketing.home.faq.bot_q", a: "marketing.home.faq.bot_a" },
  { q: "marketing.home.faq.apps_q", a: "marketing.home.faq.apps_a" },
  { q: "marketing.home.faq.languages_q", a: "marketing.home.faq.languages_a" },
  { q: "marketing.home.faq.free_q", a: "marketing.home.faq.free_a" },
  { q: "marketing.home.faq.recordings_q", a: "marketing.home.faq.recordings_a" },
  { q: "marketing.home.faq.windows_q", a: "marketing.home.faq.windows_a" },
  { q: "marketing.home.faq.team_q", a: "marketing.home.faq.team_a" },
];

export const FaqSection = () => {
  const { t } = useI18n();
  return (
    <section aria-labelledby="home-faq" class="border-t border-primary-border px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <SectionTitle id="home-faq" text={t("marketing.home.faq.title")} linked />
        <dl class="divide-y divide-primary-border border-y border-primary-border">
          <For each={FAQ}>
            {(item) => (
              <div class="py-6">
                <dt class="text-lg font-semibold">{t(item.q)}</dt>
                <dd class="mt-2 leading-relaxed text-secondary-text">{t(item.a, signupCredits())}</dd>
              </div>
            )}
          </For>
        </dl>
      </div>
    </section>
  );
};
